import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  StorageKeys,
  strongPasswordRegex,
  termsAndConditionsText,
} from '@core/constants';
import { JWT_INSTANCE } from '@core/helpers/jwt';
import { DecodedToken } from '@core/models/decoded-token';
import { UserModel } from '@core/models/user-model';
import { AlertService } from '@core/services/alert/alert.service';
import { Login } from '@core/state/auth/auth.actions';
import {
  FinishUserFirstPassword,
  InitUserFirstPassword,
  ValidateUserFirstPasswordToken,
} from '@core/state/invitation/invitation.actions';
import {
  selectInvitationLoading,
  selectInvitationTokenValidity,
  selectRequestInProgress,
} from '@core/state/invitation/invitation.selectors';
import { PasswordValidators } from '@core/validators/password-validators';
import { Store } from '@ngrx/store';
import { CustomDialogComponent } from '@shared/components/custom-dialog/custom-dialog.component';
import {
  FormValidationMessageModule,
  PasswordRulesModule,
} from '@shipmercury/mercury-ui';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { ButtonDirective, ButtonLabel } from 'primeng/button';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { PasswordModule } from 'primeng/password';
import { Observable } from 'rxjs';
import { PublicBaseComponent } from 'src/app/public-base.component';

import { UserCreateFirstPasswordService } from './user-create-first-password.service';

export const jwt = new JwtHelperService();

@Component({
  selector: 'au-user-create-first-password',
  templateUrl: './user-create-first-password.component.html',
  styleUrls: ['./user-create-first-password.component.scss'],
  imports: [
    ReactiveFormsModule,
    PasswordModule,
    DividerModule,
    PasswordRulesModule,
    FormValidationMessageModule,
    CheckboxModule,
    ButtonDirective,
    RouterLink,
    CustomDialogComponent,
    AsyncPipe,
    RecaptchaModule,
    ButtonLabel,
  ],
})
export class UserCreateFirstPasswordComponent
  extends PublicBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;
  @ViewChild('turnstileRef') turnstileRef?: ElementRef<HTMLDivElement>;

  form!: FormGroup;
  token: string = '';
  isInvitationModelValid$: Observable<boolean>;
  isInvitationLoading$: Observable<boolean>;
  isRequestInProgress$: Observable<boolean>;
  isTokenValid: boolean = false;
  toggleTermsModal: boolean = false;
  termsAndConditionsNeeded: boolean = false;
  email: FormControl = new FormControl<string>('', [
    Validators.required,
    Validators.email,
  ]);

  mercuryLogoPath = '../../assets/images/logo-original.svg';
  jwt = inject(JWT_INSTANCE);
  strongRegex = strongPasswordRegex;
  safeTermsHtml: SafeHtml;

  constructor(
    fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userCreateFirstPasswordService: UserCreateFirstPasswordService,
    private store: Store<any>,
    private alertService: AlertService,
    private sanitizer: DomSanitizer
  ) {
    super();
    this.storageService.delete(StorageKeys.authUser);
    this.storageService.delete(StorageKeys.captchaResponse);
    this.form = fb.group(
      {
        password: [
          null,
          {
            validators: [
              Validators.required,
              PasswordValidators.containsUpperCaseCharacter,
              PasswordValidators.containsLowerCaseCharacter,
              PasswordValidators.containsSpeicalCharacter,
              PasswordValidators.containsNumericCharacter,
              Validators.minLength(8),
            ],
          },
        ],
        passwordRepeat: [
          null,
          {
            validators: [Validators.required],
          },
        ],
        checkboxControl: [false, { validators: [Validators.requiredTrue] }],
      },
      {
        validators: [PasswordValidators.passwordsMatch()],
        updateOn: 'change',
      }
    );

    this.isInvitationModelValid$ = store.select(selectInvitationTokenValidity);
    this.isInvitationLoading$ = store.select(selectInvitationLoading);
    this.isRequestInProgress$ = store.select(selectRequestInProgress);

    this.safeTermsHtml = this.sanitizer.bypassSecurityTrustHtml(
      termsAndConditionsText
    );
  }

  get password(): FormControl {
    return this.form.get('password') as FormControl;
  }

  get passwordRepeat(): FormControl {
    return this.form.get('passwordRepeat') as FormControl;
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.params['token'];
    this.userCreateFirstPasswordService
      .validateUserFirstPasswordCreationToken(this.token)
      .subscribe((res) => {
        if (res && res.success && res.data) {
          this.termsAndConditionsNeeded = res.data.termsAndConditionsNeeded;

          // bypass terms and conditions if not needed
          if (!this.termsAndConditionsNeeded) {
            this.form.get('checkboxControl')?.setValue(true);
          }

          this.isTokenValid = res.data.valid === true;
        }

        this.store.dispatch(
          ValidateUserFirstPasswordToken({
            isTokenValid: this.isTokenValid,
          })
        );
      });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.enableRecaptchaFlag) {
      return;
    }

    if (this.useTurnstile) {
      await this.initializeTurnstile(this.turnstileRef!, (token: string) =>
        this.resolved(token)
      );
    }
  }

  handleTermsChange($event: CheckboxChangeEvent) {
    let checked = $event.checked;

    if (checked) {
      this.form.get('checkboxControl')?.setValue(true);
    } else {
      this.form.get('checkboxControl')?.setValue(false);
    }
  }

  toggleTermsAndConditionsModal(toggle: boolean) {
    this.toggleTermsModal = toggle;
  }

  agreeTerms() {
    this.toggleTermsAndConditionsModal(false);
    this.form.get('checkboxControl')?.setValue(true);
  }

  submitRequestWithCaptcha(): void {
    this.captchaRef.execute();
  }

  resolved(captchaResponse: any) {
    if (captchaResponse) {
      this.storageService.save(captchaResponse, StorageKeys.captchaResponse);
      if (!this.useTurnstile) {
        this.sendCreateFirstUserPasswordRequest();
        this.captchaRef.reset();
      } else {
        this.verifiedSuccessfully = true;
      }
    }
  }

  sendCreateFirstUserPasswordRequest() {
    if (this.turnstileWidgetId) {
      this.verifiedSuccessfully = false;
      window.turnstile?.reset(this.turnstileWidgetId);
    }
    this.store.dispatch(InitUserFirstPassword({ model: {} }));

    this.userCreateFirstPasswordService
      .sendSetPasswordRequest(
        this.token,
        this.password.value,
        this.passwordRepeat.value
      )
      .subscribe((res) => {
        if (res && res.success && res.data) {
          this.saveAccessToken(res.data);

          this.router.navigateByUrl('/login');
        } else if (res && !res.success && res.errorMessage) {
          this.alertService.error({ message: res.errorMessage });
        }
        this.store.dispatch(FinishUserFirstPassword({ model: {} }));
      });
  }

  private saveAccessToken(data: any) {
    let decodedToken = this.jwt.decodeToken<DecodedToken>(data.accessToken);
    if (!decodedToken) return;

    let user: UserModel = {
      name: decodedToken.name,
      email: decodedToken.email,
      customerId: decodedToken.customerId,
      authToken: data.accessToken,
      refreshToken: data.refreshToken,
      userId: decodedToken.userId,
    };
    this.store.dispatch(Login({ user }));
  }

  ngOnDestroy(): void {
    if (this.turnstileWidgetId) {
      window.turnstile?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }
}
